/* eslint-disable max-lines */
/* eslint-disable camelcase */
/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ActionCreatorWithOptionalPayload } from '@reduxjs/toolkit';
import { fetchAllCampaignRuleOutcomes } from 'actions';
import LoadingIndicator from 'components/LoadingIndicator';
import useClickOutside from 'components/utils/useClickOutside';
import { EXTERNAL_LINK_WHITE_ICON } from 'constants/commonIcons';
import { SEARCH_ICON } from 'constants/filterIcons';
import { FiltersTreeContext } from 'contexts/ExploreFiltersTreeContext';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import LoadingOverlayWrapper from 'react-loading-overlay-ts';
import { useDispatch } from 'react-redux';
import { getCampaignRuleOutcomesList } from 'selectors/campaignRuleOutcomes';
import { useSelector } from 'store';
import { Selector, UUID } from 'types';
import logEvent from 'utils/analytics';
import { triggerURIDownload } from 'utils/triggerDownload';
import ShowLabels from './ShowLabels';

type ResourceFilterProps<N, A, S> = {
  className?: string;
  filterKey: string;
  fetchAllAction?: A;
  getResourceList: S;
  getLoading?: Selector<boolean>;
  title?: string;
  nameKey: N;
  allowExclude?: boolean;
  dropdown?: boolean;
  dataTestid?: string;
  isRule?: boolean;
  colorChip?: boolean;
  isOpening?: boolean;
  treeContext: React.Context<FiltersTreeContext>;
  sliceSize?: number;
};

const ResourceFilter = <
  N extends string,
  T extends {
    uuid: UUID;
    color?: string | null;
    envelopes_count?: number;
    count?: number;
  } & Record<N, string | null>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  A extends ActionCreatorWithOptionalPayload<any, string>,
  S extends Selector<T[], []>
>(
  props: ResourceFilterProps<N, A, S>
): ReturnType<React.FC> => {
  const {
    fetchAllAction,
    filterKey,
    getResourceList,
    getLoading,
    title = '',
    nameKey,
    allowExclude = true,
    dropdown = false,
    className,
    dataTestid,
    isRule = false,
    colorChip = false,
    isOpening,
    treeContext,
    sliceSize = 25,
  } = props;

  const dispatch = useDispatch();

  const { treeParams, filtersSearchString, handleAddValueToTree, handleRemoveValueFromTree } =
    useContext(treeContext);

  const allData = useSelector(getResourceList);
  const [isOpen, setIsOpen] = useState(false);
  const [filtered, setFiltered] = useState<T[]>([]);
  const [search, setSearch] = useState('');
  const outcomes = useSelector(getCampaignRuleOutcomesList);

  const loading = useSelector((state) => (getLoading ? getLoading(state) : false));

  const inputRef = useRef<HTMLInputElement>(null);

  const filterData = treeParams[filterKey] || [];
  const notFilterData = treeParams[`not_${filterKey}`] || [];
  const andFilterData = treeParams[`${filterKey}_and`] || [];
  const andNotFilterData = treeParams[`not_${filterKey}_and`] || [];

  const handleClickOutside = useCallback(() => setIsOpen(false), [setIsOpen]);
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, handleClickOutside);

  const filterBySearch = (term: string): void => {
    setFiltered(
      allData.filter((item) => {
        const name = item[nameKey];
        if (name != null) return name.toLowerCase().includes(term.toLowerCase() || '');
        return '';
      })
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
    filterBySearch(e.target.value);
  };

  useEffect(() => {
    setFiltered(allData);
  }, [allData]);

  useEffect(() => {
    if (fetchAllAction != null && isOpen) {
      dispatch(fetchAllAction({ limit: -1, include_envelopes_count: false }));
      if (isRule) {
        dispatch(fetchAllCampaignRuleOutcomes({ limit: -1 }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fetchAllAction, title, isRule, filtersSearchString, isOpen]);

  useEffect(() => {
    if (isOpening) {
      inputRef.current?.focus();
    }
  }, [inputRef, isOpening]);

  const handleButtonClick = (): void => {
    if (isOpen) {
      return;
    }
    setIsOpen(true);
  };

  const handleDelete = (labelId: string, exclude: boolean): void => {
    if (exclude) {
      handleRemoveValueFromTree(labelId, `not_${filterKey}`.replace('_and', ''));
    }
    handleRemoveValueFromTree(labelId, filterKey.replace('_and', ''));
  };

  const handleInputChange = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    logEvent(`envelopes-list-filter-${title.toLowerCase()}`);

    const { shiftKey } = event.nativeEvent;
    const { id: state } = event.currentTarget;

    let exclude = shiftKey;
    if (allowExclude === false) exclude = false;

    if (exclude && filterData.includes(state)) return;
    if (!exclude && notFilterData.includes(state)) return;
    let itemsNames: string[] = [];

    if (exclude) {
      itemsNames = [...notFilterData];
    } else {
      itemsNames = [...filterData];
    }

    if (dropdown) {
      const itemsFilterData = typeof filterData === 'string' ? [filterData] : [...filterData];
      const itemsNotFilterData =
        typeof notFilterData === 'string' ? [notFilterData] : [...notFilterData];
      itemsNames = !exclude ? itemsFilterData : itemsNotFilterData;
    }

    event.currentTarget.dataset.checked =
      event.currentTarget.dataset.checked === 'true' ? 'false' : 'true';

    if (event.currentTarget.dataset.checked === 'true') {
      itemsNames.push(state);
      const label = filtered.find((item) => item.uuid === state)?.[nameKey];

      handleAddValueToTree(
        state,
        exclude ? `not_${filterKey}`.replace('_and', '') : filterKey.replace('_and', ''),
        label || ''
      );
    } else {
      itemsNames = itemsNames.filter((s) => s !== state);
      handleDelete(state, exclude);
    }

    event.stopPropagation();
  };
  const sortOptions = (a: T, b: T): number => {
    const filterIds = [...filterData, ...andFilterData, ...notFilterData, ...andNotFilterData];

    if (filterIds.includes(a.uuid) && !filterIds.includes(b.uuid)) {
      return -1;
    }
    if (filterIds.includes(b.uuid)) {
      return 1;
    }
    return 0;
  };

  const handleExportCounts = (): void => {
    const shapedData = allData.map((d) => ({
      name: d[nameKey],
      count: !d.count ? 0 : d.count,
    }));

    const stringData = shapedData.reduce((acc, val) => `${acc}${val.name},${val.count}\n`, '');
    const formattedData = `data:text/csv;charset=utf-8,Name,Count\n${stringData}`;

    triggerURIDownload(formattedData, title);
  };

  return (
    <div ref={wrapperRef} className={`flex flex-col ${className || ''}"`}>
      <div className="relative flex flex-row justify-start items-center w-full min-h-8 rounded p-1.5 bg-litlingo-success-light bg-opacity-10">
        <div className="w-5">{SEARCH_ICON('white')}</div>
        <input
          id="search"
          name="search"
          ref={inputRef}
          value={search}
          className="max-h-5 w-full text-white ml-1 font-normal text-base bg-transparent placeholder-white placeholder-italic"
          placeholder={`Search ${title}`}
          type="text"
          data-testid={dataTestid}
          onFocus={handleButtonClick}
          onChange={handleSearchChange}
          autoComplete="off"
        />
      </div>
      {isOpen && (
        <>
          <LoadingOverlayWrapper active={loading} spinner={<LoadingIndicator />} fadeSpeed={0}>
            <div className="w-full max-h-filter z-10 overflow-auto bg-litlingo-gray-1 custom-scrollbar">
              <fieldset className="m-2 min-h-5">
                {filtered
                  .sort(sortOptions)
                  .slice(0, sliceSize)
                  .map((item, idx) => {
                    const ruleOutcome = outcomes.find((outcome) => outcome.rule_uuid === item.uuid);
                    if (
                      isRule &&
                      (!ruleOutcome || (ruleOutcome && ruleOutcome.campaign?.deleted_at))
                    )
                      return null;

                    return (
                      <div key={item.uuid} className={idx !== 0 ? 'mt-2' : ''}>
                        <div
                          id={item.uuid}
                          role="button"
                          aria-hidden
                          className="relative flex items-center cursor-pointer"
                          data-checked={[
                            ...filterData,
                            ...notFilterData,
                            ...andFilterData,
                            ...andNotFilterData,
                          ].includes(item.uuid)}
                          onClick={handleInputChange}
                        >
                          <div className="w-full relative flex flex-col items-start justify-center text-small cursor-pointer">
                            <div className="w-full relative flex flex-row gap-1 justify-start items-start">
                              <input
                                id={item.uuid}
                                data-testid={`${item.uuid}-checkbox`}
                                type="checkbox"
                                className={`form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out cursor-pointer  ${
                                  notFilterData.includes(item.uuid) ||
                                  andNotFilterData.includes(item.uuid)
                                    ? 'litlingo-checkbox-negated'
                                    : ''
                                }`}
                                checked={
                                  filterData.includes(item.uuid) ||
                                  notFilterData.includes(item.uuid) ||
                                  andNotFilterData.includes(item.uuid) ||
                                  andFilterData.includes(item.uuid)
                                }
                              />
                              {colorChip && (
                                <div
                                  className="rounded-full min-w-3 min-h-3 mr-2"
                                  style={{ backgroundColor: item.color || '' }}
                                />
                              )}
                              <div className="w-full flex flex-col">
                                <label
                                  htmlFor={`${filterKey}-${item.uuid}`}
                                  className="pt-0.5 w-full flex flex-row justify-between gap-1 text-small cursor-pointer"
                                >
                                  <div className="flex flex-col">
                                    <span
                                      className="w-full select-none"
                                      style={{ wordBreak: 'break-word' }}
                                    >
                                      {item[nameKey]}
                                    </span>
                                  </div>
                                  <span className="text-litlingo-primary">
                                    {!item.count && item.count !== 0 ? '-' : item.count}
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </fieldset>
            </div>
          </LoadingOverlayWrapper>
          {allowExclude && isOpen && (
            <div className="mt-2 -mb-2 flex flex-row justify-between items-center">
              <button type="button" className="focus:outline-none" onClick={handleExportCounts}>
                {EXTERNAL_LINK_WHITE_ICON}
              </button>
              <span className="text-white font-normal text-xss">Shift + click to exclude</span>
            </div>
          )}
        </>
      )}
      {!isOpen && (
        <ShowLabels
          handleDelete={handleDelete}
          getResourceList={getResourceList}
          filterData={typeof filterData === 'string' ? [filterData] : filterData}
          notFilterData={notFilterData as string[]}
          andFilterData={andFilterData as string[]}
          andNotFilterData={andNotFilterData as string[]}
          nameKey={nameKey}
        />
      )}
    </div>
  );
};

export default ResourceFilter;
